import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Eye,
  EyeOff,
  Loader2,
  ArrowRight,
  ShieldCheck,
  Shield,
  ArrowLeft,
  HelpCircle,
  MessageSquare,
  Mail,
  X,
  Smartphone,
  KeyRound,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Clock,
  Lock
} from "lucide-react";
import { useAuth } from "./AuthContext";
import { API_BASE } from "../config/api";
import { useGoogleAuth } from "./useGoogleAuth";
import { GoogleConfigModal } from "./GoogleConfigModal";
import { formatAuthErrorMessage } from "./authErrorUtils";

import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { GoogleAuthIcon } from "../components/ui/GoogleAuthIcon";
import { OtpCodeInput } from "../components/ui/OtpCodeInput";
export { GoogleAuthIcon };

interface LoginPageProps {
  onGoRegister: () => void;
  onGoForgot: () => void;
  onGoReset2Fa?: () => void;
  isModal?: boolean;
  onClose?: () => void;
}

export function LoginPage({ onGoRegister, onGoForgot, onGoReset2Fa, isModal, onClose }: LoginPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, verify2FA, isLoading, error, clearError } = useAuth();

  const getTargetRedirect = useCallback(() => {
    const fromState = (location.state as any)?.from;
    if (fromState) {
      if (typeof fromState === 'string') return fromState;
      if (fromState.pathname) {
        return `${fromState.pathname}${fromState.search || ''}${fromState.hash || ''}`;
      }
    }
    const queryRedirect = searchParams.get('redirect') || searchParams.get('returnTo');
    if (queryRedirect && queryRedirect.startsWith('/')) {
      return queryRedirect;
    }
    return '/dashboard';
  }, [location.state, searchParams]);
  const {
    triggerGoogleLogin,
    isGoogleLoading,
    googleError,
    clearGoogleError,
    showConfigModal,
    setShowConfigModal
  } = useGoogleAuth();

  // Nettoyer les erreurs au montage et au démontage, et s'assurer d'arriver au sommet absolu de la page
  useEffect(() => {
    if (!isModal) {
      window.scrollTo({ left: 0, top: 0, behavior: 'instant' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
    clearError();
    clearGoogleError();
    return () => {
      clearError();
      clearGoogleError();
    };
  }, [clearError, clearGoogleError, isModal]);

  const handleGoRegister = () => {
    clearError();
    clearGoogleError();
    onGoRegister();
  };

  const handleGoForgot = () => {
    clearError();
    clearGoogleError();
    onGoForgot();
  };

  const handleGoReset2Fa = () => {
    clearError();
    clearGoogleError();
    if (onGoReset2Fa) onGoReset2Fa();
  };

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);

  const displayError = formatAuthErrorMessage(googleError || error, "");

  const handleGoogleLogin = async () => {
    clearError();
    clearGoogleError();
    const result = await triggerGoogleLogin();
    if (result && result.requires2FA && result.tempToken) {
      setTempToken(result.tempToken);
      setRequires2FA(true);
      setTwoFactorCode("");
    } else if (result && !result.requires2FA) {
      if (onClose) onClose();
      navigate(getTargetRedirect(), { replace: true });
    }
  };

  // Étape 2FA
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [showLostAccessModal, setShowLostAccessModal] = useState(false);
  const [isRequestingReset, setIsRequestingReset] = useState(false);
  const [resetRequestSuccess, setResetRequestSuccess] = useState<string | null>(null);
  const [resetRequestError, setResetRequestError] = useState<string | null>(null);
  const [generatedResetToken, setGeneratedResetToken] = useState<string | null>(null);
  const [resetResponse, setResetResponse] = useState<{
    success?: boolean;
    emailSent?: boolean;
    emailError?: string;
    ipUnauthorized?: boolean;
    unauthorizedIp?: string;
    maskedEmail?: string;
    token?: string;
    isTestMode?: boolean;
    alreadyInCoolingOff?: boolean;
    unlocksAt?: string;
    message?: string;
  } | null>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (requires2FA && codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, [requires2FA]);

  const handleSubmitLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    try {
      const result = await login(email, password);
      if (result && result.requires2FA && result.tempToken) {
        setTempToken(result.tempToken);
        setRequires2FA(true);
        setTwoFactorCode("");
      } else if (result && !result.requires2FA) {
        if (onClose) onClose();
        navigate(getTargetRedirect(), { replace: true });
      }
    } catch {
      // L'erreur est gérée dans AuthContext (setError) et affichée dans la notification
    }
  };

  const handleVerify2FA = async (e?: React.FormEvent, codeToVerify?: string) => {
    if (e) e.preventDefault();
    clearError();
    const code = codeToVerify || twoFactorCode;
    if (!tempToken || !code.trim() || code.trim().length !== 6) return;
    try {
      await verify2FA(tempToken, code.trim());
      if (onClose) onClose();
      navigate(getTargetRedirect(), { replace: true });
    } catch {
      // L'erreur est gérée dans AuthContext
    }
  };

  const handleBackToLogin = () => {
    setRequires2FA(false);
    setTempToken("");
    setTwoFactorCode("");
    clearError();
  };

  const handleRequest2FaReset = async () => {
    setIsRequestingReset(true);
    setResetRequestSuccess(null);
    setResetRequestError(null);
    setResetResponse(null);
    try {
      const res = await fetch(`${API_BASE}/auth/2fa/request-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Impossible d\'initier la réinitialisation');
      }

      setResetResponse(data);
      if (data.token) {
        setGeneratedResetToken(data.token);
      }
      setResetRequestSuccess(data.message || 'Demande enregistrée avec succès !');
    } catch (err: any) {
      setResetRequestError(err.message || 'Erreur lors de la demande');
    } finally {
      setIsRequestingReset(false);
    }
  };

  return (
    <div className={isModal ? "w-full" : "min-h-screen min-h-[100dvh] w-full flex flex-col justify-center items-center bg-white px-4 sm:px-10 py-6 sm:py-10"}>
      <div className={isModal ? "w-full space-y-4" : "w-full max-w-[400px] my-auto space-y-7 py-2"}>

        {/* Logo (uniquement en pleine page) */}
        {!isModal && (
          <div className="text-center flex flex-col items-center">
            <Link to="/" state={{ manualNav: true }} className="inline-block cursor-pointer transition-transform hover:scale-[1.02]">
              <img src="/logo.webp" alt="Zeyilo" className="h-10 sm:h-11 w-auto object-contain mb-1.5" />
            </Link>
            <p className="text-slate-500 text-sm">Agent IA WhatsApp</p>
          </div>
        )}

        {!requires2FA ? (
          <div className="space-y-2.5 animate-in fade-in duration-200">
            {/* Header Bento : Uniquement en pleine page, masqué dans le modal compact */}
            {!isModal && (
              <div className="bg-slate-100/80 rounded-2xl p-3.5 sm:p-4 border-0 shadow-none">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white text-emerald-600 flex items-center justify-center shrink-0 border-0 shadow-none">
                    <Lock className="w-5 h-5 shrink-0" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-xs sm:text-sm font-bold text-slate-900 leading-tight">
                      Connexion sécurisée
                    </h2>
                    <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                      Accédez à votre agent IA WhatsApp & vos conversations
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Bento Card sans border : Connexion Google & Formulaire */}
            <div className={`p-3.5 sm:p-4 border-0 shadow-none space-y-2.5 ${isModal ? 'bg-slate-50/80 rounded-2xl' : 'bg-white rounded-xl sm:rounded-2xl'}`}>
              {/* Google Social Login */}
              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={isLoading || isGoogleLoading}
                className="w-full flex items-center justify-center gap-2.5 bg-white hover:bg-slate-100/80 disabled:opacity-60 disabled:cursor-not-allowed text-slate-800 font-semibold text-xs sm:text-sm py-2 px-3.5 rounded-xl transition-all cursor-pointer shadow-2xs border-0 active:scale-[0.99]"
              >
                {isGoogleLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin text-slate-600" />
                ) : (
                  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                )}
                <span>{isGoogleLoading ? "Connexion avec Google..." : "Continuer avec Google"}</span>
              </button>

              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-slate-200/60" />
                <span className="text-[10px] text-slate-400 font-medium">ou avec vos identifiants</span>
                <div className="flex-1 h-px bg-slate-200/60" />
              </div>

              {/* Formulaire classique */}
              <form onSubmit={handleSubmitLogin} className="space-y-2">
                {displayError && (
                  <div className="p-2 bg-rose-50 text-rose-700 text-xs font-medium rounded-xl flex items-center gap-2 border-0 animate-in fade-in">
                    <AlertCircle className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                    <span>{displayError}</span>
                  </div>
                )}

                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-slate-700">Adresse e-mail</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="vous@votreboutique.com"
                    required
                    className="w-full px-3 py-2 bg-white rounded-xl text-xs sm:text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-900 border-0 shadow-2xs transition-all"
                  />
                </div>

                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <label className="text-[11px] font-bold text-slate-700">Mot de passe</label>
                    <button type="button" onClick={handleGoForgot} className="text-[10.5px] text-emerald-600 hover:text-emerald-700 font-medium transition-colors cursor-pointer">
                      Mot de passe oublié ?
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      type={showPw ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      className="w-full px-3 py-2 bg-white rounded-xl text-xs sm:text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-900 border-0 shadow-2xs transition-all pr-9"
                    />
                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                      {showPw ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-slate-900 hover:bg-black active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-xs sm:text-sm py-2 sm:py-2.5 px-4 rounded-full flex items-center justify-center gap-1.5 transition-all cursor-pointer shadow-xs border-0 mt-1"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Connexion en cours...</span>
                    </>
                  ) : (
                    <>
                      <span>Se connecter</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Footer Bento : Inscription */}
            <div className="p-2 bg-slate-100/70 rounded-xl text-center text-xs text-slate-600 border-0">
              <span>Pas encore de compte ? </span>
              <button
                type="button"
                onClick={handleGoRegister}
                className="text-emerald-600 hover:text-emerald-700 font-bold transition-colors cursor-pointer"
              >
                Créer un compte gratuit
              </button>
            </div>
          </div>
        ) : (
          /* STEP 2: 2FA Google Authenticator Verification - Clean Bento Relance/Horaire Design */
          <div className="space-y-3 sm:space-y-3.5 animate-in fade-in slide-in-from-right-3 duration-200">
            {/* Header Bento : Google Authenticator Status */}
            <div className="bg-slate-100/80 rounded-2xl p-3.5 sm:p-4 border-0 shadow-none">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white text-emerald-600 flex items-center justify-center shrink-0 border-0 shadow-none">
                  <GoogleAuthIcon className="w-5 h-5 shrink-0" />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-xs sm:text-sm font-bold text-slate-900 leading-tight">
                    Google Authenticator
                  </h2>
                  <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                    Validation de sécurité requise après connexion
                  </p>
                </div>
              </div>

              {email && (
                <div className="mt-2.5 pt-2.5 border-t border-slate-200/50 flex items-center justify-between text-[11px]">
                  <span className="text-slate-500">Compte vérifié :</span>
                  <span className="font-semibold text-slate-800 truncate max-w-[210px]">{email}</span>
                </div>
              )}
            </div>

            {/* Sous-Bento blanc : Saisie du code à 6 chiffres */}
            <form onSubmit={handleVerify2FA} className="bg-white rounded-xl p-3.5 sm:p-4 border-0 shadow-none space-y-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] font-bold text-slate-700">
                  Code de vérification
                </span>
                <span className="text-[10px] text-slate-400">
                  Expire toutes les 30s
                </span>
              </div>

              {error && (
                <div className="p-2.5 bg-rose-50 text-rose-700 text-xs font-medium rounded-xl flex items-center gap-2 border-0 animate-in fade-in">
                  <AlertCircle className="w-4 h-4 text-rose-500 shrink-0" />
                  <span>{formatAuthErrorMessage(error, "Code de vérification 2FA invalide.")}</span>
                </div>
              )}

              <OtpCodeInput
                value={twoFactorCode}
                onChange={setTwoFactorCode}
                onComplete={(code) => handleVerify2FA(undefined, code)}
                disabled={isLoading}
                hasError={!!error}
                autoFocus={true}
              />

              <button
                type="submit"
                disabled={isLoading || twoFactorCode.trim().length < 6}
                className="w-full bg-slate-900 hover:bg-slate-800 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-xs sm:text-sm py-2.5 sm:py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-all cursor-pointer shadow-xs border-0"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Validation du code...</span>
                  </>
                ) : (
                  <>
                    <span>Valider le code</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            {/* Bento Card : Liste d'étapes claires (sans surcharge) */}
            <div className="bg-slate-100/80 rounded-2xl p-3 sm:p-3.5 border-0 shadow-none space-y-2">
              <div className="flex items-center gap-2">
                <Smartphone className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                <span className="text-[11px] font-bold text-slate-700">Étapes de validation :</span>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2.5 text-[11px] text-slate-600 bg-white/70 rounded-lg px-2.5 py-1.5">
                  <span className="w-4 h-4 rounded-md bg-white text-slate-700 text-[10px] font-bold flex items-center justify-center shrink-0 shadow-2xs">1</span>
                  <span>Ouvrez votre application <strong>Google Authenticator</strong></span>
                </div>
                <div className="flex items-center gap-2.5 text-[11px] text-slate-600 bg-white/70 rounded-lg px-2.5 py-1.5">
                  <span className="w-4 h-4 rounded-md bg-white text-slate-700 text-[10px] font-bold flex items-center justify-center shrink-0 shadow-2xs">2</span>
                  <span>Consultez le code à 6 chiffres pour <strong>Zeyilo</strong></span>
                </div>
                <div className="flex items-center gap-2.5 text-[11px] text-slate-600 bg-white/70 rounded-lg px-2.5 py-1.5">
                  <span className="w-4 h-4 rounded-md bg-white text-slate-700 text-[10px] font-bold flex items-center justify-center shrink-0 shadow-2xs">3</span>
                  <span>Saisissez-le ci-dessus pour ouvrir votre session</span>
                </div>
              </div>
            </div>

            {/* Actions secondaires */}
            <div className="space-y-2 pt-1">
              <button
                type="button"
                onClick={() => {
                  setResetRequestSuccess(null);
                  setResetRequestError(null);
                  setGeneratedResetToken(null);
                  setResetResponse(null);
                  setShowLostAccessModal(true);
                }}
                className="w-full py-2.5 px-3 rounded-xl bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 text-xs font-semibold transition-all cursor-pointer flex items-center justify-center gap-1.5 border border-slate-200/70 shadow-xs active:scale-[0.99]"
              >
                <HelpCircle className="w-3.5 h-3.5 text-slate-400" />
                <span>Code inaccessible ou téléphone perdu ?</span>
              </button>

              <button
                type="button"
                onClick={handleBackToLogin}
                className="w-full text-center text-xs font-semibold text-emerald-600 hover:text-emerald-700 flex items-center justify-center gap-1.5 py-1.5 transition-colors cursor-pointer"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Retour à la saisie du mot de passe</span>
              </button>
            </div>
          </div>
        )}

      </div>

      {/* Modal Assistance Perte d'Accès Bento */}
      {showLostAccessModal && (
        <div className="fixed inset-0 z-50 bg-slate-950/40 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-150">
          <div className="bg-slate-100/95 rounded-2xl border-0 max-w-md w-full p-4 sm:p-5 space-y-3.5 shadow-2xl animate-in zoom-in-95 duration-150">
            {/* Header Bento */}
            <div className="bg-white rounded-xl p-3.5 sm:p-4 flex items-start justify-between gap-3 border-0 shadow-none">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center shrink-0">
                  <HelpCircle className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-xs sm:text-sm font-bold text-slate-950">Accès 2FA indisponible ?</h3>
                  <p className="text-[11px] text-slate-500 mt-0.5">Procédure de récupération sécurisée</p>
                </div>
              </div>
              <button
                onClick={() => setShowLostAccessModal(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {resetRequestError && (
              <div className="p-3 bg-rose-50 text-rose-800 text-xs font-medium rounded-xl flex items-center gap-2 border-0">
                <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                <span>{resetRequestError}</span>
              </div>
            )}

            {resetRequestSuccess ? (
              <div className="bg-white rounded-xl p-4 border-0 shadow-none space-y-3.5 text-xs">
                {resetResponse?.alreadyInCoolingOff ? (
                  /* Cas 1: Déjà en période 24h */
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sky-700 font-bold">
                      <Clock className="w-4 h-4 text-sky-600 shrink-0" />
                      <span>Période de sécurité 24h déjà en cours</span>
                    </div>
                    <p className="text-slate-600 text-[11px] leading-relaxed">
                      Une demande de déblocage est déjà active pour votre compte. Le délai incompressible de 24h protège actuellement vos données contre toute intrusion.
                    </p>
                    {resetResponse.unlocksAt && (
                      <div className="p-2.5 bg-slate-50 rounded-lg text-[11px] text-slate-700 border border-slate-100">
                        Déblocage automatique le : <strong>{new Date(resetResponse.unlocksAt).toLocaleString('fr-FR', { dateStyle: 'full', timeStyle: 'short' })}</strong>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => setShowLostAccessModal(false)}
                      className="w-full py-2.5 px-3 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl transition-all cursor-pointer border-0"
                    >
                      <span>J'ai compris (Fermer)</span>
                    </button>
                  </div>
                ) : resetResponse?.emailSent ? (
                  /* Cas 2: E-mail envoyé avec succès (Production Sécurisée) */
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-emerald-800 font-bold">
                      <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
                      <span>Lien de sécurité envoyé par e-mail !</span>
                    </div>
                    <p className="text-slate-600 text-[11px] leading-relaxed">
                      Un e-mail contenant votre lien de confirmation sécurisé (valable 1 heure) a été envoyé à <strong>{resetResponse.maskedEmail || email}</strong>.
                    </p>
                    <div className="bg-emerald-50 text-emerald-950 rounded-lg p-2.5 text-[11px] space-y-1">
                      <p className="font-semibold text-emerald-900">Procédure de déblocage :</p>
                      <p>1. Ouvrez votre boîte de réception e-mail.</p>
                      <p>2. Cliquez sur <strong>« Confirmer la demande »</strong> dans l'e-mail reçu.</p>
                      <p>3. Le délai de protection de 24h démarrera dès votre clic.</p>
                    </div>

                    <button
                      type="button"
                      onClick={() => setShowLostAccessModal(false)}
                      className="w-full py-2.5 px-3 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl transition-all cursor-pointer border-0"
                    >
                      <span>Consulter mes e-mails (Fermer)</span>
                    </button>

                    {generatedResetToken && (
                      <div className="pt-1 border-t border-slate-100 text-center">
                        <button
                          type="button"
                          onClick={() => {
                            setShowLostAccessModal(false);
                            if (generatedResetToken) {
                              window.location.href = `/login?token=${encodeURIComponent(generatedResetToken)}`;
                            }
                          }}
                          className="text-[10px] text-slate-400 hover:text-slate-600 underline cursor-pointer"
                        >
                          [Mode Développeur] Accéder au lien de validation en direct
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Cas 3: Mode Test / Envoi d'e-mail bloqué par Brevo */
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-amber-800 font-bold">
                      <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                      <span>Mode Test : Lien direct généré</span>
                    </div>

                    {resetResponse?.ipUnauthorized ? (
                      <div className="bg-amber-50 text-amber-900 rounded-lg p-2.5 text-[11px] space-y-1 border border-amber-200/60">
                        <p className="font-semibold text-amber-950">ℹ️ Service e-mail Brevo en attente d'autorisation IP :</p>
                        <p className="text-[10.5px]">
                          L'adresse IP de ce serveur VPS (<strong>{resetResponse.unauthorizedIp || '5.189.191.3'}</strong>) doit être autorisée sur Brevo (<a href="https://app.brevo.com/security/authorised_ips" target="_blank" rel="noreferrer" className="underline font-bold text-amber-900 hover:text-amber-950">Authorised IPs</a>) pour acheminer les e-mails en réel.
                        </p>
                        <p className="text-[10.5px] pt-1 text-slate-600">
                          En attendant, vous pouvez tester l'étape de validation complète directement :
                        </p>
                      </div>
                    ) : (
                      <p className="text-slate-600 text-[11px] leading-relaxed">
                        Le lien de sécurité a été créé avec succès en base de données. Vous pouvez tester la validation directement :
                      </p>
                    )}

                    <button
                      type="button"
                      onClick={() => {
                        setShowLostAccessModal(false);
                        if (generatedResetToken) {
                          window.location.href = `/login?token=${encodeURIComponent(generatedResetToken)}`;
                        }
                      }}
                      className="w-full py-2.5 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-1.5 shadow-xs cursor-pointer border-0 active:scale-[0.99]"
                    >
                      <span>Tester la validation (« Oui c'est moi »)</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-xl p-3.5 sm:p-4 border-0 shadow-none space-y-3">
                <div className="space-y-2">
                  <span className="text-[11px] font-bold text-slate-700 block">
                    Protocole de sécurité en 3 points :
                  </span>
                  <div className="space-y-1.5 text-[11px] text-slate-600">
                    <div className="flex items-start gap-2 bg-slate-50 p-2 rounded-lg">
                      <span className="w-4 h-4 rounded-md bg-white text-slate-700 text-[10px] font-bold flex items-center justify-center shrink-0">1</span>
                      <span>Demande envoyée avec confirmation par email</span>
                    </div>
                    <div className="flex items-start gap-2 bg-slate-50 p-2 rounded-lg">
                      <span className="w-4 h-4 rounded-md bg-white text-slate-700 text-[10px] font-bold flex items-center justify-center shrink-0">2</span>
                      <span>Délai de sécurité de <strong>24 heures</strong> pour protéger vos données</span>
                    </div>
                    <div className="flex items-start gap-2 bg-slate-50 p-2 rounded-lg">
                      <span className="w-4 h-4 rounded-md bg-white text-slate-700 text-[10px] font-bold flex items-center justify-center shrink-0">3</span>
                      <span>Désactivation automatique du 2FA à expiration</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 pt-1">
                  <button
                    type="button"
                    disabled={isRequestingReset}
                    onClick={handleRequest2FaReset}
                    className="w-full py-2.5 sm:py-3 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white font-bold text-xs flex items-center justify-center gap-2 transition-all cursor-pointer shadow-xs border-0 active:scale-[0.99]"
                  >
                    {isRequestingReset ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Génération du lien sécurisé...</span>
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4" />
                        <span>Initier la réinitialisation (délai 24h)</span>
                      </>
                    )}
                  </button>

                  <a
                    href={`https://wa.me/2290129647182?text=${encodeURIComponent(`Bonjour Zeyilo Support, j'ai perdu l'accès à mon Google Authenticator pour mon compte : ${email}`)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="w-full py-2 px-3 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-semibold text-xs flex items-center justify-center gap-2 transition-colors cursor-pointer border-0"
                  >
                    <MessageSquare className="w-3.5 h-3.5 text-emerald-600" />
                    <span>Contacter le support WhatsApp</span>
                  </a>
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setShowLostAccessModal(false)}
                className="px-3.5 py-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 rounded-lg transition-colors cursor-pointer"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Configuration Google Auth (Si Client ID manquant) */}
      <GoogleConfigModal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
      />
    </div>
  );
}
